import { organiseColumns } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/helpers';
import { ClearOutlined } from '@mui/icons-material';
import {
  AutocompleteCloseReason,
  AutocompleteProps,
  Box,
  BoxProps,
  IconButton,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  TextFieldProps,
  Typography,
  useControlled,
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ReactNode, SyntheticEvent, useRef, useState } from 'react';
import { useMeasure, useUpdateEffect } from 'react-use';
import { UUID } from 'v2/core/entities/uuid';
import { shallow } from 'zustand/shallow';
import {
  useKeyboardNavigation,
  useMultiColumnAutoCompleteStore,
} from './hooks';
import { MultiColumnAutoCompleteActionType } from './hooks/useMultiColumnAutoCompleteStore/multiColumnAutoCompleteActionType';
import { MultiColumnDropdown } from './MultiColumnDropdown';

export interface BaseMultiColumnAutoCompleteProps<T>
  extends Omit<
    AutocompleteProps<T, false, false, false>,
    'renderTags' | 'renderOption' | 'renderInput' | 'renderGroup'
  > {
  rows: string[][];
  groupKey: keyof T;
  columnsLength?: number;
  PopperProps?: Omit<PopperProps, 'open'>;
  PaperProps?: PaperProps;
  renderInput: (textFieldProps: TextFieldProps, activeOption?: T) => ReactNode;
  renderOption?: (props: BoxProps, option: T) => ReactNode;
  renderGroup?: (option: T) => ReactNode;
}

export function BaseMultiColumnAutoComplete<T extends { id: UUID }>(
  props: BaseMultiColumnAutoCompleteProps<T>,
) {
  const {
    rows,
    groupKey,
    columnsLength = 2,
    PopperProps = {},
    PaperProps = {},
    renderInput,
    renderOption,
    options,
    inputValue = '',
    onInputChange,
    open: openProp,
    fullWidth = false,
    disabled = false,
    id = '',
    noOptionsText = 'No options',
    loading = false,
    loadingText = 'Loading…',
    onClose,
    onOpen,
    readOnly,
    onChange,
    openOnFocus = false,
    groupBy,
    renderGroup,
    className,
  } = props;

  const [open, setOpenState] = useControlled({
    controlled: openProp,
    default: false,
    name: 'useMultiColumnAutoComplete',
    state: 'open',
  });

  const dispatch = useMultiColumnAutoCompleteStore(({ dispatch }) => dispatch);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [inputRef, { width: inputWidth }] = useMeasure();
  const [optionsByGroup, setOptionsByGroup] = useState({});
  const isPopupOpen = open && !readOnly;

  const activeOption = useMultiColumnAutoCompleteStore(
    ({
      activeRowIndex,
      activeOptionIndex,
      activeColumnIndex,
      optionIDByRowColumnOptionIndex,
    }) => {
      const optionID =
        optionIDByRowColumnOptionIndex[activeRowIndex]?.[activeColumnIndex]?.[
          activeOptionIndex
        ];

      return options.find((option) => option.id === optionID);
    },
    shallow,
  );

  useUpdateEffect(() => {
    const {
      optionsByGroup,
      columnOptionLimitByRowID,
      optionIndexByOptionID,
      groupsByRowColumn,
      optionIDByRowColumnOptionIndex,
    } = organiseColumns({
      groupKey,
      rows,
      options,
      columnsLength,
    });

    setOptionsByGroup(optionsByGroup);

    dispatch({
      type: MultiColumnAutoCompleteActionType.SET,
      payload: {
        columnOptionLimitByRowID,
        optionIndexByOptionID,
        groupsByRowColumn,
        renderOption,
        groupKey,
        optionIDByRowColumnOptionIndex,
        activeOptionIndex: 0,
        activeColumnIndex: 0,
        activeRowIndex: 0,
      },
    });
  }, [options, rows, groupKey, columnsLength]);

  const handleInputChange = (event: SyntheticEvent) => {
    const newValue = (event.target as HTMLInputElement).value;

    if (inputValue !== newValue) {
      onInputChange?.(event, newValue, 'input');
    }
  };

  const handleOpen = (event: SyntheticEvent) => {
    if (open) {
      return;
    }

    setOpenState(true);
    onOpen?.(event);
  };

  const handleClose = (
    event: SyntheticEvent,
    reason: AutocompleteCloseReason,
  ) => {
    if (!open) {
      return;
    }

    setOpenState(false);
    onClose?.(event, reason);
  };

  const onKeyboardNavigation = useKeyboardNavigation({
    options,
    onChange,
    handleOpen,
    handleClose,
  });

  const onKeyDown = (event: React.KeyboardEvent) => {
    const target = event.target as typeof event.target & { id: string };
    const isPrimaryBtnPressed = target.id === 'autocomplete-primary-btn';

    if (isPrimaryBtnPressed) {
      return handleClose(event, 'toggleInput');
    }

    onKeyboardNavigation(event);
    handleOpen(event);
  };

  const handleInputMouseDown = (event: SyntheticEvent) => {
    const target = event.target as typeof event.target & { id: string };
    const isPrimaryBtnClicked = target.id === 'autocomplete-primary-btn';

    if (isPrimaryBtnClicked) {
      return handleClose(event, 'toggleInput');
    }

    if (open) {
      handleClose(event, 'toggleInput');
    } else {
      handleOpen(event);
    }
  };

  const handleInputFocus = (event: SyntheticEvent) => {
    if (openOnFocus) {
      handleOpen(event);
    }
  };

  const onClickAway = (event: MouseEvent | TouchEvent) => {
    if (!open) {
      return;
    }

    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    handleClose(event as unknown as SyntheticEvent, 'blur');
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Box
        ref={anchorRef}
        sx={{ width: fullWidth ? '100%' : undefined }}
        onKeyDown={onKeyDown}
      >
        <Box ref={inputRef}>
          {renderInput(
            {
              fullWidth,
              disabled,
              id: `${id}-input`,
              value: inputValue,
              onChange: handleInputChange,
              onMouseDown: handleInputMouseDown,
              onFocus: handleInputFocus,
              role: 'combobox',
              autoComplete: 'off',
              spellCheck: 'false',
              InputProps: {
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={(event) => onInputChange?.(event, '', 'clear')}
                  >
                    {!!inputValue ? <ClearOutlined /> : ''}
                  </IconButton>
                ),
              },
            },
            activeOption,
          )}
        </Box>
        <Popper
          {...PopperProps}
          className={className}
          open={anchorRef.current ? isPopupOpen : false}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal, ...PopperProps.sx }}
        >
          <Paper
            elevation={8}
            {...PaperProps}
            sx={{
              width: inputWidth,
              maxHeight: '40vh',
              overflow: 'auto',
              ...PaperProps.sx,
            }}
          >
            {loading ? (
              <Typography color="text.disabled" px={2} py={1.75}>
                {loadingText}
              </Typography>
            ) : (
              <MultiColumnDropdown
                groupBy={groupBy}
                optionsByGroup={optionsByGroup}
                onChange={onChange}
                renderGroup={renderGroup}
              />
            )}
            {options.length === 0 && !loading ? (
              <Typography color="text.secondary" px={2} py={1.75}>
                {noOptionsText}
              </Typography>
            ) : null}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
